/**
 * Preflight domain types — mirror of the BE ``PreflightResponse``
 * (``theplaidoyer-api/app/schemas/citation_registry.py``,
 * ``POST /api/v1/citation-registry/preflight``, api#580 / THE-460).
 *
 * The preflight endpoint partitions a batch of refKeys into three
 * lawyer-actionable buckets:
 *
 *   - ``ok``     — refKey verified AND source document is in force.
 *   - ``stale``  — refKey verified BUT source is repealed/amended OR
 *                  the fragment's validity window expired. Lawyer
 *                  should re-verify before depositing.
 *   - ``broken`` — unparseable refKey OR source not found. The chip
 *                  is unusable as-is; lawyer must edit or remove it.
 *
 * The ``summary`` block lets the pane render a ribbon footer
 * ("X ok · Y stale · Z broken") without iterating the three lists.
 *
 * W7 invariant: every status here is BE-sourced; the add-in never
 * derives ok/stale/broken locally.
 */

export type PreflightStaleReason =
  | 'document-repealed'
  | 'document-amended'
  | 'fragment-expired'

export type PreflightBrokenReason =
  | 'refkey-malformed'
  | 'article-not-found'
  | 'decision-not-found'

export interface PreflightOkEntry {
  refKey: string
  /** ISO 8601 UTC timestamp of last index — ``Document.parsed_at`` when
   *  available, else ``Document.created_at``. ``null`` for legacy rows
   *  without either signal. */
  verifiedAt: string | null
  /** Match confidence in ``[0.0, 1.0]``. 1.0 today for every exact
   *  match (BE reserves the field for future fuzzy tiers). */
  score: number
}

export interface PreflightStaleEntry {
  refKey: string
  lastCheck: string | null
  reason: PreflightStaleReason
}

export interface PreflightBrokenEntry {
  refKey: string
  reason: PreflightBrokenReason
}

export interface PreflightSummary {
  total: number
  ok: number
  stale: number
  broken: number
}

export interface PreflightReport {
  ok: PreflightOkEntry[]
  stale: PreflightStaleEntry[]
  broken: PreflightBrokenEntry[]
  summary: PreflightSummary
}

/** Empty report — sugar for ``runPreflight([])`` short-circuit + the
 *  initial state of any consumer hook. */
export function emptyPreflightReport(): PreflightReport {
  return {
    ok: [],
    stale: [],
    broken: [],
    summary: { total: 0, ok: 0, stale: 0, broken: 0 },
  }
}
